import type {
  CertificationKey,
  ChargeMethod,
  ExchangeUnit,
  PackageSource,
  PartnerProgramKind,
} from '@mntn-dev/domain-types'
import {
  ChargeMethodEnum,
  CreditUnitEnum,
  isPartnerProgramKind,
  PackageSources,
  PartnerProgramKindEnum,
} from '@mntn-dev/domain-types'
import { BrandedMap } from '@mntn-dev/utility-types'

type PackageBillingConfig = {
  billingMethods: readonly [ChargeMethod, ...ChargeMethod[]]
  nativeExchangeUnit: ExchangeUnit
  requiredPartnerProgramKind?: PartnerProgramKind
}

const PackageBillingConfigBySource = new BrandedMap<
  PackageSource,
  PackageBillingConfig
>({
  ctv: {
    billingMethods: [
      ChargeMethodEnum.mntn_credits,
      ChargeMethodEnum.invoice,
      ChargeMethodEnum.stripe,
    ],
    nativeExchangeUnit: CreditUnitEnum.mntn_credits,
    requiredPartnerProgramKind: PartnerProgramKindEnum.mntn_credits,
  },
  linkedin: {
    billingMethods: [ChargeMethodEnum.linkedin],
    nativeExchangeUnit: CreditUnitEnum.linkedin,
    requiredPartnerProgramKind: PartnerProgramKindEnum.linkedin,
  },
  meta: {
    billingMethods: [ChargeMethodEnum.meta],
    nativeExchangeUnit: CreditUnitEnum.meta,
    requiredPartnerProgramKind: PartnerProgramKindEnum.meta,
  },
  quickframe: {
    billingMethods: [ChargeMethodEnum.invoice, ChargeMethodEnum.stripe],
    nativeExchangeUnit: 'USD',
    requiredPartnerProgramKind: undefined,
  },
  tiktok: {
    billingMethods: [ChargeMethodEnum.tiktok],
    nativeExchangeUnit: CreditUnitEnum.tiktok,
    requiredPartnerProgramKind: PartnerProgramKindEnum.tiktok,
  },
})

const buildPackageSourceRecord = <T>(
  selector: (packageSource: PackageSource) => T
): BrandedMap<PackageSource, T> => BrandedMap.build(PackageSources, selector)

export const PackageBillingMethods = buildPackageSourceRecord(
  (packageSource) =>
    PackageBillingConfigBySource.get(packageSource).billingMethods
)

export const getPackagePrimaryBillingMethod = (
  packageSource: PackageSource
) => {
  return PackageBillingMethods.get(packageSource)[0]
}

export const getPackageBillingMethods = (packageSource: PackageSource) => {
  return PackageBillingMethods.get(packageSource)
}

export const getPackageSourcePartnerProgramKinds = (
  packageSource: PackageSource
) => {
  return PackageBillingMethods.get(packageSource).filter(isPartnerProgramKind)
}

export const PackageNativeExchangeUnitMap =
  buildPackageSourceRecord<ExchangeUnit>(
    (packageSource) =>
      PackageBillingConfigBySource.get(packageSource).nativeExchangeUnit
  )

/**
 * The programs that are required to participate in a project of a specific package source.
 */
export const PackageSourcePartnerProgramMap = buildPackageSourceRecord<
  PartnerProgramKind | undefined
>(
  (packageSource) =>
    PackageBillingConfigBySource.get(packageSource).requiredPartnerProgramKind
)

export const getPackagePartnerProgramKind = (
  packageSource: PackageSource | undefined
) => {
  return packageSource && PackageSourcePartnerProgramMap.get(packageSource)
}

export const PartnerProgramPackageSourceMap = BrandedMap.fromEntries<
  PartnerProgramKind,
  PackageSource
>(
  PackageSourcePartnerProgramMap.entries()
    .filter(([, kind]) => kind !== undefined)
    .map(([source, kind]) => [kind as string, source])
)

export const getPartnerProgramPackageSource = (
  partnerProgramKind: PartnerProgramKind | undefined
) => {
  if (
    !partnerProgramKind ||
    !PartnerProgramPackageSourceMap.has(partnerProgramKind)
  ) {
    return undefined
  }
  return PartnerProgramPackageSourceMap.get(partnerProgramKind)
}

/**
 * The certifications that are required to participate in a project of a specific program.
 */
export const RequiredCertificationKeyProgramKindMap = new BrandedMap<
  PartnerProgramKind,
  CertificationKey | undefined
>({
  linkedin: 'linkedin-certified',
  meta: 'meta-certified',
  tiktok: 'tiktok-certified',
  mntn_credits: 'mntn-certified',
})
